import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import {
    loadFollowers,
    loadSpaceProfilesByPublicKey,
    normalizeFollowPublicKey,
} from '../../utils/follows';
import '../MuultiStyle.css';

const shortAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

const timestampMs = (value) => {
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatWhen = (value) => {
    const ms = timestampMs(value);
    return ms ? new Date(ms).toLocaleString() : '';
};

export default function UserFollowersPage() {
    const { connected, publicKey } = useWallet();
    const [followers, setFollowers] = useState([]);
    const [profilesByPublicKey, setProfilesByPublicKey] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const walletAddress = useMemo(() => {
        try {
            return normalizeFollowPublicKey(publicKey?.toBase58?.() || publicKey?.toString?.() || '');
        } catch {
            return '';
        }
    }, [publicKey]);

    const loadFollowerList = useCallback(async () => {
        if (!walletAddress) {
            setFollowers([]);
            setProfilesByPublicKey(new Map());
            setError('');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const rows = await loadFollowers(walletAddress);
            const followerKeys = rows
                .map((row) => normalizeFollowPublicKey(row.followerPublicKey || row.id))
                .filter(Boolean);
            const profiles = await loadSpaceProfilesByPublicKey(followerKeys);
            rows.sort((a, b) => timestampMs(b.createdAt) - timestampMs(a.createdAt));
            setFollowers(rows);
            setProfilesByPublicKey(profiles);
        } catch (err) {
            console.error('Follower list load failed:', err);
            setFollowers([]);
            setProfilesByPublicKey(new Map());
            setError(err?.message || 'Unable to load followers.');
        } finally {
            setLoading(false);
        }
    }, [walletAddress]);

    useEffect(() => {
        loadFollowerList();
    }, [loadFollowerList]);

    return (
        <div className="muulti-container">
            <div className="wallet-detail-page">
                <div className="detail-page-return-link-container">
                    <Link className="muulti-button detail-page-return-link" to="/wallet-detail">
                        Back to wallet
                    </Link>
                </div>

                <section className="wallet-detail-card wallet-detail-header">
                    <div>
                        <h1>Followers</h1>
                        <p className="wallet-detail-muted">
                            {walletAddress
                                ? `Followers of ${shortAddress(walletAddress)}`
                                : 'Connect a wallet to view followers for that space.'}
                        </p>
                    </div>
                    <div className="wallet-detail-actions">
                        <WalletMultiButton />
                        {walletAddress ? (
                            <button
                                type="button"
                                className="muulti-button wallet-detail-refresh"
                                onClick={loadFollowerList}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Refresh'}
                            </button>
                        ) : null}
                    </div>
                </section>

                {!connected || !walletAddress ? null : (
                    <section className="wallet-detail-card">
                        <div className="wallet-detail-row wallet-detail-row--header">
                            <div>
                                <h2>Follower List</h2>
                                <p className="wallet-detail-muted">
                                    {followers.length} {followers.length === 1 ? 'space follows' : 'spaces follow'} this public key.
                                </p>
                            </div>
                        </div>

                        {error ? <p className="wallet-detail-error">{error}</p> : null}

                        {loading && !followers.length ? (
                            <p className="wallet-detail-muted">Loading followers...</p>
                        ) : followers.length ? (
                            <div className="wallet-history-list">
                                {followers.map((row) => {
                                    const followerPublicKey = normalizeFollowPublicKey(row.followerPublicKey || row.id);
                                    const profile = profilesByPublicKey.get(followerPublicKey) || null;
                                    const profileName = profile?.muultiName || row.followerMuultiName || '';
                                    const title = profile?.pageTitle || profileName || shortAddress(followerPublicKey);
                                    const profileHref = profileName ? `/${encodeURIComponent(profileName)}` : '';

                                    return (
                                        <div className="wallet-history-row" key={followerPublicKey}>
                                            <div className="wallet-history-main">
                                                <span className="wallet-history-type">Follower</span>
                                                <strong>{title}</strong>
                                                <span className="wallet-detail-muted">{followerPublicKey}</span>
                                                {row.createdAt ? (
                                                    <span className="wallet-detail-muted">
                                                        Followed {formatWhen(row.createdAt)}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="wallet-history-meta">
                                                {profile?.profilePicUrl ? (
                                                    <img
                                                        className="wallet-follower-avatar"
                                                        src={profile.profilePicUrl}
                                                        alt={title}
                                                    />
                                                ) : null}
                                                {profileHref ? (
                                                    <Link to={profileHref}>
                                                        View Space
                                                    </Link>
                                                ) : (
                                                    <span className="wallet-detail-muted">No MuultiSpace profile</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="wallet-detail-muted">
                                No followers found for this public key yet.
                            </p>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}
